import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useContent } from "../../context/ContentContext";

const GoogleG = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

function Stars({ rating = 5, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= rating ? "fill-[#FBBC05] text-[#FBBC05]" : "text-white/20"} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { t } = useLang();
  const { content } = useContent();
  const reviews = content?.reviews || [];
  const googleUrl = content?.google_url || "";
  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((a, r) => a + (Number(r.rating) || 5), 0) / reviews.length).toFixed(1);

  return (
    <section id="reviews" className="relative py-24 md:py-32 px-5 md:px-10 border-t border-white/10" data-testid="reviews-section">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-acid mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-acid" /> {t("reviews_kicker")}
            </p>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] text-white">
              {t("reviews_title")}
            </h2>
          </div>
          <div className="flex items-center gap-5 border border-white/10 bg-surface px-6 py-4">
            <GoogleG size={34} />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-3xl text-white leading-none">{avg}</span>
                <Stars rating={Math.round(avg)} size={18} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-silver mt-1">
                {t("reviews_based")} · {reviews.length}
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id || i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="border border-white/10 bg-surface p-6 flex flex-col gap-4"
              data-testid={`review-card-${i}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 flex items-center justify-center bg-acid text-obsidian font-display text-lg">
                    {(r.author || "?").trim().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-heading text-white text-lg leading-tight">{r.author}</p>
                    {r.location && <p className="font-mono text-[10px] uppercase tracking-widest text-silver">{r.location}</p>}
                  </div>
                </div>
                <GoogleG size={20} />
              </div>
              <Stars rating={Number(r.rating) || 5} />
              <p className="text-silver text-sm leading-relaxed flex-1">"{r.text}"</p>
            </motion.div>
          ))}
        </div>

        {googleUrl && (
          <div className="mt-12 flex justify-center">
            <a
              href={googleUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 bg-acid px-8 py-4 font-heading font-semibold uppercase tracking-wide text-obsidian hover:bg-acid-hover transition-colors"
              data-testid="reviews-cta"
            >
              <GoogleG size={20} /> {t("reviews_cta")} <ExternalLink size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
